import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowUpRightIcon, MapPinIcon } from "lucide-react";
import { useHackathon } from "../../contexts/HackathonContext";
import { initials } from "../../utils/progress";
import type { Mentor } from "../../types/hackathon";
import { SectionHeading } from "./SectionHeading";
import { MentorDialog } from "./MentorDialog";
import { mentorGroups, type GroupPanel } from "./mentorCategories";

// Text colours for groups sitting directly on the dark section background.
const defaultPanel: GroupPanel = {
  bg: "",
  border: "border-sun",
  heading: "text-cream",
  accent: "text-sun",
  body: "text-cream/85",
  muted: "text-cream/60"
};
export function MentorsSection() {
  const {
    state
  } = useHackathon();
  const [selected, setSelected] = useState<Mentor | null>(null);
  const close = useCallback(() => setSelected(null), []);

  // Every group on the page is listed alphabetically by name.
  const sorted = [...state.mentors].sort((a, b) => a.name.localeCompare(b.name));
  const judges = sorted.filter(m => m.category === "judge");
  const uncategorised = sorted.filter(m => !m.category);
  return <section id="mentors" className="scroll-mt-20 bg-ink py-24 text-sun md:py-32" data-id="element-543" data-mp="plal523h7lkz">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-544" data-mp="kmpn99bp5dzp">
        <SectionHeading title="Mentors & judges" tone="light" intro="Founders, engineers, investors and the Circuit Launch team, guiding every team from first sketch to the Demo Day stage. Tap anyone for their bio and office hours." data-id="element-545" data-mp="koaiz29qz2z5" />

        {judges.length > 0 && <div className="mt-16 md:mt-20" data-id="element-546" data-mp="18ituy7xrd88g">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-t-2 border-sun pt-5" data-id="element-547" data-mp="13m3irvp2qwsb">
              <h3 className="font-display text-4xl uppercase text-cream md:text-5xl" data-id="element-548" data-mp="2117ldmgrlrxi">Demo Day judges</h3>
              <p className="text-[12px] font-bold uppercase tracking-wide text-sun" data-id="element-549" data-mp="1w97suf30y1ay">
                Tue Oct 13 · Pitches 2 PM PT · On site at Circuit Launch
              </p>
            </div>
            <ul className="mt-8 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-550" data-mp="2k5ucrw6dtyxu">
              {judges.map(m => <li key={m.id} data-id="element-551" data-mp="26dech34v8f0m">
                  <button type="button" onClick={() => setSelected(m)} className="group flex w-full flex-col text-left" data-id="element-552" data-mp="26cd2z33i00l0">
                    <Portrait mentor={m} tile="bg-tomato text-ink" className="aspect-[4/5] text-[clamp(4rem,9vw,8rem)]" data-id="element-553" data-mp="1wicpacu5hn0o" />
                    <span className="block pt-5" data-id="element-554" data-mp="1nflfehgxazb5">
                      <span className="block font-display text-[clamp(2rem,3.2vw,3rem)] uppercase leading-[0.95] text-cream" data-id="element-555" data-mp="1nh4tc1f3wy9r">
                        {m.name}
                      </span>
                      <span className="mt-3 block text-[15px] text-cream/75" data-id="element-556" data-mp="3hunurmc9d10c">{m.title}</span>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-sun" data-id="element-557" data-mp="3ltn0va9q73td">
                        View profile
                        <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-558" data-mp="166j1g76ozlrc" />
                      </span>
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}

        {mentorGroups.map(g => {
        const people = sorted.filter(m => m.category && g.categories.includes(m.category));
        const list = g.id === "mentors" ? [...people, ...uncategorised].sort((a, b) => a.name.localeCompare(b.name)) : people;
        if (list.length === 0) return null;
        const p = g.panel ?? defaultPanel;
        return <div key={g.id} className={`mt-20 md:mt-24 ${g.panel ? `${p.bg} -mx-5 rounded-[32px] px-5 pb-12 pt-8 md:-mx-0 md:px-10 md:pb-14 md:pt-10` : ""}`} data-id="element-559" data-mp="8jaavxru3mih">
              <div className={`grid gap-3 border-t-2 ${p.border} pt-5 md:grid-cols-[1fr_minmax(0,420px)] md:items-baseline`} data-id="element-560" data-mp="5wrjwyiluvi0">
                <h3 className={`font-display text-4xl uppercase md:text-5xl ${p.heading}`} data-id="element-561" data-mp="9xl71po8469u">
                  {g.title}
                </h3>
                <p className={`text-[15px] ${p.muted}`} data-id="element-562" data-mp="jxc3hwwcgvck">{g.intro}</p>
              </div>
              <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4" data-id="element-563" data-mp="3if7wxrkjyg2l">
                {list.map(m => <li key={m.id} data-id="element-564" data-mp="3nf2m9qooixlj">
                    <button type="button" onClick={() => setSelected(m)} className="group block w-full text-left" data-id="element-565" data-mp="3nby8q2pbqpi7">
                      <Portrait mentor={m} tile={g.tile} className="aspect-square text-[clamp(3rem,7vw,5.5rem)]" data-id="element-566" data-mp="yg6yex0k4vk" />
                      <span className={`mt-4 block font-display text-2xl uppercase leading-tight group-hover:underline md:text-3xl ${p.heading}`} data-id="element-567" data-mp="2zp8a7bc6pd2c">
                        {m.name}
                      </span>
                      <span className={`mt-1 block text-[12px] font-semibold uppercase tracking-wide ${p.accent}`} data-id="element-568" data-mp="2x4k1tjqr822s">{m.expertise}</span>
                      <span className={`mt-1 block text-sm ${p.muted}`} data-id="element-569" data-mp="2s5yyvck4b4an">{m.organization}</span>
                      {m.location && <span className={`mt-2 flex items-center gap-1.5 text-[13px] font-medium ${p.body}`} data-id="element-570" data-mp="2i71bwe2cbitr">
                          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-571" data-mp="2e5xyrzbog0w8" />
                          {m.location}
                        </span>}
                      {m.note && <span className={`mt-2 block text-[13px] font-medium ${g.panel ? p.heading : "text-tomato"}`} data-id="element-572" data-mp="24fd1qgbnj4ff">{m.note}</span>}
                    </button>
                  </li>)}
              </ul>
            </div>;
      })}
      </div>

      <AnimatePresence data-id="element-573" data-mp="1ujjfu6c1lykz">{selected && <MentorDialog mentor={selected} onClose={close} data-id="element-574" data-mp="1qg3ptsuhawh9" />}</AnimatePresence>
    </section>;
}
function Portrait({
  mentor,
  tile,
  className
}: {
  mentor: Mentor;
  tile: string;
  className: string;
}) {
  return <span className={`block overflow-hidden rounded-[28px] ${tile} ${className}`} data-id="element-575" data-mp="1kucm5m0ihkid">
      {mentor.photo ? <img src={mentor.photo} alt={mentor.name} style={mentor.photoPosition ? {
      objectPosition: mentor.photoPosition
    } : undefined} className="h-full w-full object-cover transition-transform duration-200 ease-out group-hover:scale-[1.03]" data-id="element-576" data-mp="16utqhsow01my" /> : <span className="flex h-full items-center justify-center font-display leading-none transition-transform duration-200 ease-out group-hover:scale-[1.04]" aria-hidden="true" data-id="element-577" data-mp="23j9ehkccm2xy">
          {initials(mentor.name)}
        </span>}
    </span>;
}